import { createContext, useCallback, useContext, useMemo } from "react";
import type { PermissionNames, PermissionOperations } from "~enums/permissions";
import type { UserRoles } from "~enums/userRoles";
import useGetCurrentUserPermissionScope from "~hooks/useGetCurrentUserPermissionScope";
import type { PermissionConfig } from "~types/permissionConfig.ts";

type UserPermissionsContextType = {
  hasOrganizationAccess: boolean;
  permissions: Record<string, Array<string>>;
  userRoles: UserRoles[];
  isLoadingPermissions: boolean;
  hasPermission: (permissionName: PermissionNames, permissionOperation: PermissionOperations) => boolean;
  hasEveryPermission: (permissionConfig: PermissionConfig[]) => boolean;
  hasAnyPermission: (permissionConfig: PermissionConfig[]) => boolean;
};

const UserPermissionsContext = createContext<UserPermissionsContextType | null>(null);

export const UserPermissionsProvider = ({ children }) => {
  const { data: scopeData, isLoading: isLoadingPermissions } = useGetCurrentUserPermissionScope();
  const userRoles = scopeData?.scope?.userRoles || [];
  const rbacMatrix = useMemo<Record<string, Array<string>>>(
    () =>
      (scopeData?.scope?.rbacLines || []).reduce((accumulator, currentValue) => {
        accumulator[currentValue.resource] = currentValue.operations;
        return accumulator;
      }, {}),
    [scopeData?.scope?.rbacLines],
  );
  const hasOrganizationAccess = useMemo(() => Object.keys(rbacMatrix).length > 0, [rbacMatrix]);

  const hasEveryPermission = useCallback(
    (permissionConfig: PermissionConfig[]): boolean =>
      permissionConfig.every(({ permissionName, permissionOperations }) => {
        if (permissionName in rbacMatrix) {
          return permissionOperations.every((option) => rbacMatrix[permissionName]?.includes(option));
        }
        return false;
      }),
    [rbacMatrix],
  );

  const hasAnyPermission = useCallback(
    (permissionConfig: PermissionConfig[]): boolean =>
      permissionConfig.some(({ permissionName, permissionOperations }) => {
        if (permissionName in rbacMatrix) {
          return permissionOperations.every((option) => rbacMatrix[permissionName]?.includes(option));
        }
        return false;
      }),
    [rbacMatrix],
  );

  const hasPermission = useCallback(
    (permissionName: PermissionNames, permissionOperation: PermissionOperations): boolean => {
      return hasAnyPermission([{ permissionName, permissionOperations: [permissionOperation] }]);
    },
    [hasAnyPermission],
  );

  return (
    <UserPermissionsContext.Provider
      value={{
        hasOrganizationAccess,
        permissions: rbacMatrix,
        isLoadingPermissions,
        hasPermission,
        hasEveryPermission,
        hasAnyPermission,
        userRoles,
      }}
    >
      {children}
    </UserPermissionsContext.Provider>
  );
};

export const useUserPermissions = (): UserPermissionsContextType => {
  const context = useContext(UserPermissionsContext);
  if (!context) {
    throw new Error("useUserPermissions must be used within a UserPermissionsProvider");
  }
  return context;
};
